import { useState } from 'react';
import { Form } from 'react-bootstrap';
import IconAlert from '@/components/common/IconAlert';
import { ScheduleWebhookBucketFilters } from './ScheduleWebhookBucketFilters';
import { WebhookEventHelpTip } from './WebhookEventHelpTip';
import {
  INSPECT_PER_URL_WEBHOOK_EVENTS,
  INSPECT_WEBHOOK_COMPLETED,
  hasInspectPerUrlWebhook,
} from '../lib/inspect-webhook-events';
import { inspectScheduleEventGuide } from '../lib/inspect-schedule-event-guide';
import { getCookie, setCookie } from '../lib/cookies';

const PER_URL_EVENT_IDS = [...INSPECT_PER_URL_WEBHOOK_EVENTS];
const EVENTS_INTRO_COOKIE = 'indexflow.inspectScheduleEventsIntroDismissed';

function isEventsIntroDismissed(): boolean {
  return getCookie(EVENTS_INTRO_COOKIE) === '1';
}

function dismissEventsIntro() {
  setCookie(EVENTS_INTRO_COOKIE, '1');
}

export function InspectScheduleWebhookForm({
  events,
  filters,
  autoIndexNotIndexed,
  onEventsChange,
  onFiltersChange,
  onAutoIndexNotIndexedChange,
}: {
  events: string[];
  filters: string[];
  autoIndexNotIndexed: boolean;
  onEventsChange: (events: string[]) => void;
  onFiltersChange: (filters: string[]) => void;
  onAutoIndexNotIndexedChange: (value: boolean) => void;
}) {
  const [introVisible, setIntroVisible] = useState(() => !isEventsIntroDismissed());
  const completedGuide = inspectScheduleEventGuide(INSPECT_WEBHOOK_COMPLETED);
  const perUrlEnabled = hasInspectPerUrlWebhook(events);

  function closeIntro() {
    dismissEventsIntro();
    setIntroVisible(false);
  }

  function toggle(eventId: string) {
    const next = events.includes(eventId)
      ? events.filter((id) => id !== eventId)
      : [...events, eventId];

    if (hasInspectPerUrlWebhook(events) && !hasInspectPerUrlWebhook(next)) {
      onFiltersChange([]);
    }
    onEventsChange(next);
  }

  function eventCheck(eventId: string) {
    const guide = inspectScheduleEventGuide(eventId);
    if (!guide) return null;
    return (
      <Form.Check
        key={eventId}
        type="checkbox"
        id={`schedule-event-${eventId}`}
        className={eventId === INSPECT_WEBHOOK_COMPLETED ? 'mb-2' : undefined}
        label={
          <>
            <span>{guide.label}</span>
            <WebhookEventHelpTip title={guide.label} paragraphs={guide.help} />
          </>
        }
        checked={events.includes(eventId)}
        onChange={() => toggle(eventId)}
      />
    );
  }

  return (
    <div className="mb-3">
      <Form.Label className="mb-2">رویدادها</Form.Label>

      {introVisible && (
        <IconAlert
          variant="info"
          dismissible
          onClose={closeIntro}
          className="mb-3"
        >
          <p className="fw-semibold mb-2">
            فرض کنید ۳ صفحه دارید و همه را چک می‌کنید:
          </p>
          <ul className="mb-0 ps-3 d-flex flex-column gap-1">
            <li>
              <strong>پایان کل</strong> — فقط یک پیام بعد از اتمام همهٔ صفحات
            </li>
            <li>
              <strong>وضعیت فعلی</strong> — برای هر صفحه یک پیام (اینجا ۳ پیام)،
              حتی اگر وضعیت عوض نشده باشد
            </li>
            <li>
              <strong>فقط وقتی عوض شد</strong> — پیام فقط وقتی وضعیت صفحه نسبت
              به قبل تغییر کند؛ بدون تغییر، بدون پیام
            </li>
          </ul>
        </IconAlert>
      )}

      {completedGuide && eventCheck(INSPECT_WEBHOOK_COMPLETED)}

      <div className="inspect-webhook-per-url-group border rounded p-3">
        <Form.Text className="text-secondary d-block mb-2 fw-semibold">
          برای هر آدرس (جداگانه)
        </Form.Text>
        <div className="d-grid gap-2">
          {PER_URL_EVENT_IDS.map((eventId) => eventCheck(eventId))}
        </div>

        {perUrlEnabled && (
          <div className="mt-3 pt-3 border-top">
            <ScheduleWebhookBucketFilters
              title="محدودسازی وضعیت"
              filters={filters}
              onChange={onFiltersChange}
              persianOnly
            />
          </div>
        )}
      </div>

      {perUrlEnabled && (
        <Form.Text className="text-secondary d-block mt-2 small">
          اگر وضعیتی انتخاب نکنید، هنگام ذخیره همهٔ وضعیت‌ها (ایندکس‌شده،
          ایندکس‌نشده، خطا) فعال می‌شوند.
        </Form.Text>
      )}

      <div className="mt-3 pt-3 border-top">
        <Form.Check
          type="checkbox"
          id="inspect-schedule-auto-index-not-indexed"
          label={
            <>
              <span>ارسال درخواست ایندکس برای آدرس‌های نوایندکس</span>
              <WebhookEventHelpTip
                title="ارسال ایندکس برای نوایندکس"
                paragraphs={[
                  'بعد از هر اجرای زمان‌بندی، آدرس‌هایی که نتیجهٔ بازرسی‌شان «ایندکس‌نشده» باشد برای ایندکس به گوگل فرستاده می‌شوند.',
                  'فقط بازرسی‌های موفق با وضعیت نوایندکس شامل می‌شوند؛ خطاهای API ارسال نمی‌شوند.',
                ]}
              />
            </>
          }
          checked={autoIndexNotIndexed}
          onChange={(e) => onAutoIndexNotIndexedChange(e.target.checked)}
        />
      </div>
    </div>
  );
}
